import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { fetchWrapper } from "../helpers";
import { message } from "antd";
import {useTranslation} from "react-i18next";
import {ensureInstagramCompliant, remuxMovMp4IfPossible} from "../helpers/videoEncodingHelper";

const name = "post";
const createInitialState = () => {
  return {
    post: null,
    loading: false,
    generating: false,
  };
};

const createExtraActions = () => {

  const baseUrl = `${process.env.REACT_APP_API_URL}`;

  const getPost = () => {
    return createAsyncThunk(
      `${name}/post`,
      async (contentPlanId) =>
        await fetchWrapper.get(`${baseUrl}/post/actual/${contentPlanId}`)
    );
  };

  const generatePost = () => {
    return createAsyncThunk(`${name}/postCreate`, async ({contentPlanId, reqBody}) => {
      return await fetchWrapper.post(
        `${baseUrl}/post/generate/${contentPlanId}`,reqBody
      );
    });
  };

  const generateImage = () => {
    return createAsyncThunk(
      `${name}/generateImage`,
      async ({ postId, contentPlanId }) => {
        await fetchWrapper.post(`${baseUrl}/post/generate-image/${postId}`);
        return await fetchWrapper.get(
          `${baseUrl}/post/actual/${contentPlanId}`
        );
      }
    );
  };

  const removeImage = () => {
    return createAsyncThunk(
        `${name}/removeImage`,
        async ({ postId, contentPlanId }) => {
          await fetchWrapper.delete(`${baseUrl}/post/remove-image/${postId}`);
          return await fetchWrapper.get(
              `${baseUrl}/post/actual/${contentPlanId}`
          );
        }
    );
  };

  const updateImages = () => {
    return createAsyncThunk(
        `${name}/updateImages`,
        async ({ postId, contentPlanId, images }) => {
          await fetchWrapper.put(`${baseUrl}/post/update-images/${postId}`, {
            images: images
          });
          return await fetchWrapper.get(
              `${baseUrl}/post/actual/${contentPlanId}`
          );
        }
    );
  };

  const uploadImage = () => {
    return createAsyncThunk(
      `${name}/uploadImage`,
      async ({ postId, contentPlanId, images }) => {
        const messages = {
          success: {
            ru: "Изображение успешно загружено",
            en: "The image has been successfully uploaded",
            kk: "Сурет сәтті жүктелді",
          },
          error: {
            ru: "ERROR! Ошибка при загрузке изображения: каждый файл ≤ 8МБ и общий объём ≤ 80МБ",
            en: "ERROR! Image upload failed: each file must be ≤ 8MB and total ≤ 80MB",
            kk: "ERROR! Суретті жүктеу сәтсіз: әр файл 8МБ-тан аспау керек, жалпысы 80МБ-тан аспау керек",
      },
        };
        const PER_FILE_LIMIT = 8 * 1024 * 1024;
        const TOTAL_LIMIT    = 80 * 1024 * 1024;
        const formData = new FormData();
        // Frontend guard (defensive): enforce per-file and total limits
        let total = 0;
        for (const image of images) {
          if (image.size > PER_FILE_LIMIT) {
            throw new Error(messages.error.en);
          }
          total += image.size || 0;
        }
        if (total > TOTAL_LIMIT) {
          throw new Error(messages.error.en);
        }
        images.forEach((image) => formData.append("images", image));
        // formData.append("images", images);
        await fetchWrapper.post(`${baseUrl}/post/upload-image/${postId}`, formData);
        return await fetchWrapper.get(`${baseUrl}/post/actual/${contentPlanId}`);
      }
    );

  };

  const uploadVideo = () => {
    return createAsyncThunk(
        `${name}/uploadVideo`,
        async ({ postId, contentPlanId, video }) => {
          const messages = {
            success: {
              ru: "Видео успешно загружено",
              en: "Video successfully uploaded",
              kk: "Бейне сәтті жүктелді",
            },
            error: {
              ru: "ERROR! Ошибка при загрузке видео, возможно размер файлов превышает 10мб либо продолжительность видео превышает 30 секунд",
              en: "ERROR! Error when uploading video, maybe file size exceeds 10mb or video duration exceeds 30 seconds",
              kk: "ERROR! Бейнені жүктеу кезінде қате, мүмкін файлдардың өлшемі 10 Мб-тан асады немесе бейненің ұзақтығы 30 секундтан асады",
            },
          };
          // Quick guardrails in case the file is too large :VIDEOFILELIMITER
          if (video.size > 80 * 1024 * 1024) {
            message.warning({
              en: 'Video is too large',
              ru: 'Слишком большой файл',
              kk: 'Файл тым үлкен' }[localStorage.getItem('ln')])
          }
          // Try client remux (HEVC is skipped and will be handled server-side)
          let fileToSend = video;
          try {
            const hide = message.loading({en: 'Optimizing video...', ru: 'Оптимизация видео', kk: 'Құрылғыда оңтайландырылды'}[localStorage.getItem('ln')]);
            let {file, changed, reason} = await remuxMovMp4IfPossible(video);
            const encodedFile = await ensureInstagramCompliant(file);
            hide();
            file = encodedFile.file;
            if (changed) {
              fileToSend = file;
              console.info('[remux] sending MP4:', fileToSend.name, fileToSend.type, fileToSend.size);
              message.info({en: 'Optimized on device', ru: 'Оптимизировано на устройстве', kk: 'Құрылғыда оңтайландырылды'}[localStorage.getItem('ln')]);
            } else if (reason?.startsWith('unsupported-codec-')) {
              // Likely HEVC/HDR - will be handled server-side
              // Or we could optimize short clips here. It will take more time though, and Safari doesn't like it.
              message.info({en: `Optimization failed: ${reason}`, ru: `Оптимизация не удалась: ${reason}`, kk: `Оңтайландыру сәтсіз аяқталды: ${reason}`}[localStorage.getItem('ln')]);
            }
          } catch (e) {
            // Silent fallback to original
            console.warn('Client remux failed, using original file... ', e);
          }

          const formData = new FormData();
          // images.forEach((image) => formData.append("images", image));
          formData.append("video", fileToSend);
          await fetchWrapper.post(
              `${baseUrl}/post/upload-video/${postId}`,
              formData
          ).then((response)=>{
            if (response){
              message.success(messages.success[localStorage.getItem('ln')] || messages.success.en);
            }else {
              message.error(messages.error[localStorage.getItem('ln')] || messages.error.en);
            }
          }).catch((err)=>{
            message.error(messages.error[localStorage.getItem('ln')] || messages.error.en);
            console.error(err);
          });

          return await fetchWrapper.get(
              `${baseUrl}/post/actual/${contentPlanId}`
          );
        }
    );
  };
  const removeVideo = () => {
    return createAsyncThunk(
        `${name}/removeVideo`,
        async ({ postId, contentPlanId }) => {
          await fetchWrapper.delete(`${baseUrl}/post/remove-video/${postId}`);
          return await fetchWrapper.get(
              `${baseUrl}/post/actual/${contentPlanId}`
          );
        }
    );
  };

  const updatePost = () => {
    return createAsyncThunk(
      `${name}/updatePost`,
      async ({ postId, contentPlanId, postData }) => {
        await fetchWrapper.put(`${baseUrl}/post/update/${postId}`, postData);
        return await fetchWrapper.get(
          `${baseUrl}/post/actual/${contentPlanId}`
        );
      }
    );
  };

  const regeneratePost = () => {
    return createAsyncThunk(
        `${name}/regeneratePost`,
        async ({ postId, contentPlanId, postData }) => {
          await fetchWrapper.post(`${baseUrl}/post/regenerate/${contentPlanId}`, postData);
          return await fetchWrapper.get(
              `${baseUrl}/post/actual/${contentPlanId}`,
          )
        }
    )
  }

  return {
    getPost: getPost(),
    generatePost: generatePost(),
    generateImage: generateImage(),
    updateImages: updateImages(),
    uploadImage: uploadImage(),
    uploadVideo: uploadVideo(),
    removeVideo: removeVideo(),
    removeImage: removeImage(),
    updatePost: updatePost(),
    regeneratePost: regeneratePost()
  };
};

const createExtraReducers = (builder) => {
  const getPost = () => {
    const { pending, fulfilled, rejected } = extraActions.getPost;
    builder.addCase(pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fulfilled, (state, action) => {
      if (action.payload?.status) {
        state.loading = false;
        state.generating = true;
      } else {
        state.post = action.payload;
        state.loading = false;
        state.generating = false;
      }
    });
    builder.addCase(rejected, (state, action) => {
      state.post = null;
      state.loading = false;
    });
  };

  const uploadImagePost = () => {
    const { pending, fulfilled, rejected } = extraActions.uploadImage;
    builder.addCase(pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fulfilled, (state, action) => {
      state.post = action.payload;
      state.loading = false;
    });
    builder.addCase(rejected, (state, action) => {
      state.post = null;
      state.loading = false;
    });
  };

  const generateImagePost = () => {
    const { pending, fulfilled, rejected } = extraActions.generateImage;
    builder.addCase(pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fulfilled, (state, action) => {
      state.post = action.payload;
      state.loading = false;
    });
    builder.addCase(rejected, (state, action) => {
      state.post = null;
      state.loading = false;
    });
  };

  const updateImagesPost = () => {
    const { pending, fulfilled, rejected } = extraActions.updateImages;
    builder.addCase(pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fulfilled, (state, action) => {
      state.post = action.payload;
      state.loading = false;
    });
    builder.addCase(rejected, (state, action) => {
      state.post = null;
      state.loading = false;
    });
  };

  const removeImagePost = () => {
    const { pending, fulfilled, rejected } = extraActions.removeImage;
    builder.addCase(pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fulfilled, (state, action) => {
      state.post = action.payload;
      state.loading = false;
    });
    builder.addCase(rejected, (state, action) => {
      state.loading = false;
    });
  };

  const createPost = () => {
    const { pending, fulfilled, rejected } = extraActions.generatePost;
    builder.addCase(pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fulfilled, (state, action) => {
      state.loading = false;
      state.generating = true;
    });
    builder.addCase(rejected, (state, action) => {
      state.loading = false;
    });
  };

  const updatePostHandler = () => {
    const { pending, fulfilled, rejected } = extraActions.updatePost;
    builder.addCase(pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fulfilled, (state, action) => {
      state.post = action.payload;
      state.loading = false;
    });
    builder.addCase(rejected, (state, action) => {
      state.loading = false;
    });
  };

  const regeneratePostHandler = () => {
       const { pending, fulfilled, rejected } = extraActions.regeneratePost;
       builder.addCase(pending, (state) => {
           state.loading = true;
         });
       builder.addCase(fulfilled, (state) => {
           // After we fire /post/regenerate/:id the backend starts async generation.
               // We mark "generating" to show a spinner/notice while waiting for polling/getPost.
                   state.loading = false;
           state.generating = true;
         });
       builder.addCase(rejected, (state) => {
           state.loading = false;
         });
     };
  const uploadVideoPost = () => {
    const { pending, fulfilled, rejected } = extraActions.uploadVideo;
    builder.addCase(pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fulfilled, (state, action) => {
      state.post = action.payload;   // update post with new video
      state.loading = false;
    });
    builder.addCase(rejected, (state) => {
      state.loading = false;         // keep old post so UI doesn’t blank out
    });
  };

  const removeVideoPost = () => {
    const { pending, fulfilled, rejected } = extraActions.removeVideo;
    builder.addCase(pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fulfilled, (state, action) => {
      state.post = action.payload;   // post without video
      state.loading = false;
    });
    builder.addCase(rejected, (state) => {
      state.loading = false;
    });
  };

  getPost();
  createPost();
  uploadImagePost();
  generateImagePost();
  updateImagesPost();
  removeImagePost();
  updatePostHandler();
  regeneratePostHandler();
  uploadVideoPost();
  removeVideoPost()
};

const extraActions = createExtraActions();
const slice = createSlice({
  name: name,
  initialState: createInitialState(),
  reducers: {
    resetPost: (state) => {
      state.post = null;
      state.loading = true; // покажем спиннер пока ждём новый fetch
      state.generating = false;
    }
  },
  extraReducers: (builder) => {
    const extraReducers = createExtraReducers(builder);
  },
});

export const postActions = { ...slice.actions, ...extraActions };
export const postReducer = slice.reducer;
